import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Trash2, Eye, MessageSquare, FileText, X, LogOut, Download, Heart, Star } from "lucide-react";
import { GestionStatus } from "../components/GestionStatus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("submissions");
  const [submissions, setSubmissions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const saved = sessionStorage.getItem("admin_pw");
    if (saved) { setPassword(saved); setAuthed(true); }
  }, []);

  useEffect(() => { if (authed) loadAll(); }, [authed]);

  const hdrs = () => ({ headers: { "X-Admin-Password": password } });

  const loadAll = async () => {
    try {
      const [s, m, sv] = await Promise.all([
        axios.get(`${API}/admin/submissions`, hdrs()),
        axios.get(`${API}/admin/messages`, hdrs()),
        axios.get(`${API}/admin/satisfaction-surveys`, hdrs()),
      ]);
      setSubmissions(s.data); setMessages(m.data); setSurveys(sv.data);
    } catch { toast.error("Error cargando datos"); }
  };

  const login = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("admin_pw", password);
      setAuthed(true);
      toast.success("Bienvenida");
    } catch { toast.error("Contraseña incorrecta"); }
  };

  const logout = () => { sessionStorage.removeItem("admin_pw"); setAuthed(false); setPassword(""); };

  const openDetail = async (sid) => {
    try {
      const r = await axios.get(`${API}/admin/submissions/${sid}`, hdrs());
      setDetail(r.data);
    } catch { toast.error("Error"); }
  };

  const del = async (type, id) => {
    if (!window.confirm("¿Eliminar?")) return;
    try {
      await axios.delete(`${API}/admin/${type}/${id}`, hdrs());
      toast.success("Eliminado");
      loadAll();
    } catch { toast.error("Error"); }
  };

  const downloadPdf = async (sub) => {
    try {
      const res = await axios.get(`${API}/admin/submissions/${sub.id}/pdf`, { ...hdrs(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cuestionario_${(sub.patient_name || "paciente").replace(/\s+/g, "_")}_${sub.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch {
      toast.error("Error al generar el PDF");
    }
  };

  const resendGestion = (kind, setter) => async (sub) => {
    try {
      const r = await axios.post(`${API}/admin/${kind}/${sub.id}/gestion-resend`, {}, hdrs());
      setter((prev) => prev.map((s) => (s.id === sub.id ? { ...s, ...r.data } : s)));
      if (r.data.gestion_status === "enviado") toast.success("Enviado a la app de gestión");
      else toast.error(`No se pudo enviar: ${r.data.gestion_last_error || "error"}. Se reintentará automáticamente.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al reenviar");
    }
  };

  const downloadConsentPdf = async (sub) => {
    try {
      const res = await axios.get(`${API}/admin/submissions/${sub.id}/consent-pdf`, { ...hdrs(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `consentimiento_${(sub.patient_name || "paciente").replace(/\s+/g, "_")}_${sub.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Consentimiento descargado");
    } catch {
      toast.error("Error al generar el consentimiento");
    }
  };

  if (!authed) {
    return (
      <main className="min-h-[70vh] flex items-center justify-center px-6 py-20" data-testid="admin-login">
        <form onSubmit={login} className="soft-card w-full max-w-md">
          <Lock size={32} style={{ color: "var(--sage-500)" }} />
          <h1 className="section-title mt-4" style={{ fontSize: "2rem" }}>Panel Admin</h1>
          <p className="font-sans-body text-sm text-[color:var(--ink-soft)] mt-2">Acceso reservado al equipo de Salud y Visión.</p>
          <label className="field-label mt-6">Contraseña</label>
          <input type="password" className="field-input" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-password" />
          <button type="submit" className="btn-primary mt-5 w-full justify-center" data-testid="admin-login-btn">Entrar</button>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-[70vh] py-12 px-6" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8 flex-wrap gap-3">
          <div>
            <span className="section-eyebrow">Administración</span>
            <h1 className="section-title mt-2" style={{ fontSize: "2rem" }}>Panel de <span className="handwritten">gestión</span></h1>
          </div>
          <button onClick={logout} className="btn-ghost" data-testid="admin-logout"><LogOut size={16} /> Salir</button>
        </div>

        <div className="flex gap-3 mb-6 font-sans-body flex-wrap">
          <button onClick={() => setTab("submissions")} className={`px-4 py-2 rounded-full text-sm ${tab === "submissions" ? "btn-primary" : "btn-ghost"}`} data-testid="tab-submissions"><FileText size={14} /> Cuestionarios ({submissions.length})</button>
          <button onClick={() => setTab("messages")} className={`px-4 py-2 rounded-full text-sm ${tab === "messages" ? "btn-primary" : "btn-ghost"}`} data-testid="tab-messages"><MessageSquare size={14} /> Mensajes ({messages.length})</button>
          <button onClick={() => setTab("surveys")} className={`px-4 py-2 rounded-full text-sm ${tab === "surveys" ? "btn-primary" : "btn-ghost"}`} data-testid="tab-surveys"><Heart size={14} /> Encuestas ({surveys.length})</button>
        </div>

        {tab === "submissions" && (
          <div className="soft-card p-0 overflow-hidden">
            <table className="w-full font-sans-body text-sm">
              <thead style={{ background: "var(--sage-50)" }}>
                <tr><th className="p-3 text-left">Paciente</th><th className="p-3 text-left">Cuestionario</th><th className="p-3 text-left">Email</th><th className="p-3 text-left">Fecha</th><th className="p-3 text-left">App gestión</th><th className="p-3"></th></tr>
              </thead>
              <tbody>
                {submissions.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-[color:var(--ink-soft)]">Sin envíos</td></tr>}
                {submissions.map((s) => (
                  <tr key={s.id} className="border-t border-[color:var(--sage-100)]">
                    <td className="p-3">{s.patient_name}</td>
                    <td className="p-3">{s.questionnaire_title}</td>
                    <td className="p-3">{s.patient_email}</td>
                    <td className="p-3 text-xs text-[color:var(--ink-soft)]">{new Date(s.created_at).toLocaleString("es-ES")}</td>
                    <td className="p-3"><GestionStatus sub={s} onResend={resendGestion("submissions", setSubmissions)} /></td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <button onClick={() => openDetail(s.id)} className="mr-2" title="Ver" data-testid={`view-${s.id}`}><Eye size={16} style={{ color: "var(--sage-600)" }} /></button>
                      <button onClick={() => downloadPdf(s)} className="mr-2" title="Descargar PDF cuestionario" data-testid={`pdf-${s.id}`}><Download size={16} style={{ color: "var(--sage-600)" }} /></button>
                      <button onClick={() => downloadConsentPdf(s)} className="mr-2" title="Descargar PDF consentimiento" data-testid={`consent-pdf-${s.id}`}><FileText size={16} style={{ color: "var(--sage-600)" }} /></button>
                      <button onClick={() => del("submissions", s.id)} title="Eliminar" data-testid={`del-sub-${s.id}`}><Trash2 size={16} style={{ color: "var(--terracotta, #c98466)" }} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "messages" && (
          <div className="space-y-3">
            {messages.length === 0 && <p className="text-center text-[color:var(--ink-soft)] font-sans-body">Sin mensajes</p>}
            {messages.map((m) => (
              <div key={m.id} className="soft-card">
                <div className="flex justify-between items-start gap-3 flex-wrap">
                  <div>
                    <div className="font-semibold">{m.name}</div>
                    <div className="text-xs text-[color:var(--ink-soft)] font-sans-body">{m.email} · {m.phone}</div>
                    <div className="font-sans-body text-xs text-[color:var(--ink-soft)] mt-1">{new Date(m.created_at).toLocaleString("es-ES")}</div>
                    {m.subject && <div className="mt-2 font-medium">{m.subject}</div>}
                    <p className="mt-2 font-sans-body text-sm">{m.message}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <GestionStatus sub={m} onResend={resendGestion("messages", setMessages)} />
                    <button onClick={() => del("messages", m.id)} data-testid={`del-msg-${m.id}`}><Trash2 size={16} style={{ color: "var(--terracotta, #c98466)" }} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "surveys" && (
          <div className="space-y-3">
            {surveys.length === 0 && <p className="text-center text-[color:var(--ink-soft)] font-sans-body">Sin encuestas recibidas</p>}
            {surveys.map((sv) => {
              const a = sv.answers || {};
              return (
                <div key={sv.id} className="soft-card" data-testid={`survey-card-${sv.id}`}>
                  <div className="flex justify-between items-start gap-3 flex-wrap mb-3">
                    <div>
                      <div className="font-semibold flex items-center gap-2">
                        {a.centro && <span className="text-xs px-2 py-1 rounded-full" style={{ background: "var(--sage-100)", color: "var(--sage-700)" }}>{a.centro}</span>}
                        {a.tiempo && <span className="text-xs px-2 py-1 rounded-full font-sans-body font-normal" style={{ background: "var(--bg-soft)", color: "var(--ink-soft)" }}>{a.tiempo}</span>}
                      </div>
                      <div className="text-xs text-[color:var(--ink-soft)] font-sans-body mt-1">
                        {sv.contact_name && <span>{sv.contact_name} · </span>}
                        {sv.contact_email && <span>{sv.contact_email} · </span>}
                        {new Date(sv.created_at).toLocaleString("es-ES")}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {a.valoracion_global && (
                        <div className="flex items-center gap-1 px-3 py-1.5 rounded-full" style={{ background: "var(--sage-500)", color: "var(--cream)" }}>
                          <Star size={14} fill="currentColor" /> <span className="font-semibold">{a.valoracion_global}/10</span>
                        </div>
                      )}
                      <button onClick={() => del("satisfaction-surveys", sv.id)} data-testid={`del-survey-${sv.id}`}><Trash2 size={16} style={{ color: "var(--terracotta, #c98466)" }} /></button>
                    </div>
                  </div>

                  {/* Resumen visual de las 5 estrellas */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs font-sans-body mb-3">
                    {[
                      ["atencion_equipo", "Atención"],
                      ["claridad_explicaciones", "Claridad"],
                      ["seguimiento_familia", "Seguimiento"],
                      ["instalaciones", "Instalaciones"],
                      ["organizacion_puntualidad", "Organización"],
                    ].map(([key, label]) => (
                      <div key={key} className="p-2 rounded-lg" style={{ background: "var(--bg-soft)" }}>
                        <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--sage-600)" }}>{label}</div>
                        <div className="flex gap-0.5 mt-1">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <Star
                              key={n}
                              size={11}
                              style={{
                                color: n <= (a[key] || 0) ? "#d4a017" : "var(--sage-200)",
                                fill: n <= (a[key] || 0) ? "#f5c542" : "transparent",
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Resto de respuestas */}
                  <details className="font-sans-body text-sm">
                    <summary className="cursor-pointer" style={{ color: "var(--sage-600)" }}>Ver todas las respuestas</summary>
                    <div className="mt-3 space-y-2">
                      {[
                        ["mejoras_notadas", "¿Han notado mejoras?"],
                        ["areas_cambio", "Áreas con cambios"],
                        ["valoracion_terapeuta", "Valoración terapeuta"],
                        ["informacion_evolucion", "Información de evolución"],
                        ["material_casa", "Material para casa"],
                        ["recomendaria", "¿Nos recomienda?"],
                        ["mas_valorado", "Lo más valorado"],
                        ["mejoras_sugeridas", "Mejoras sugeridas"],
                      ].map(([key, label]) => {
                        const v = a[key];
                        if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) return null;
                        return (
                          <div key={key} className="flex gap-3">
                            <span className="text-xs uppercase tracking-wider min-w-[140px]" style={{ color: "var(--sage-600)" }}>{label}</span>
                            <span style={{ color: "var(--ink)" }}>{Array.isArray(v) ? v.join(", ") : v}</span>
                          </div>
                        );
                      })}
                    </div>
                  </details>
                </div>
              );
            })}
          </div>
        )}

        {detail && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(31,41,20,0.6)" }} onClick={() => setDetail(null)}>
            <div className="bg-[color:var(--bg)] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="submission-detail">
              <div className="flex justify-between items-start mb-4 gap-3">
                <div>
                  <h2 className="text-2xl font-medium">{detail.submission.patient_name}</h2>
                  <p className="font-sans-body text-sm text-[color:var(--ink-soft)]">{detail.submission.questionnaire_title} · {new Date(detail.submission.created_at).toLocaleString("es-ES")}</p>
                </div>
                <div className="flex gap-2 items-center">
                  <button onClick={() => downloadPdf(detail.submission)} className="btn-primary text-sm py-2 px-4" data-testid="detail-pdf-btn"><Download size={14} /> Cuestionario</button>
                  <button onClick={() => downloadConsentPdf(detail.submission)} className="btn-ghost text-sm py-2 px-4" data-testid="detail-consent-btn"><FileText size={14} /> Consentimiento</button>
                  <button onClick={() => setDetail(null)} data-testid="detail-close"><X size={24} /></button>
                </div>
              </div>
              {detail.questionnaire?.sections.map((sec, i) => (
                <div key={i} className="mb-5 pb-4 border-b border-[color:var(--sage-100)]">
                  <h3 className="font-semibold text-[color:var(--sage-600)] mb-2">{sec.title}</h3>
                  <div className="space-y-2 font-sans-body text-sm">
                    {sec.fields.map((f) => {
                      const val = detail.submission.answers[f.id];
                      if (val === undefined || val === "" || val === null) return null;
                      let display;
                      if (typeof val === "object") display = <pre className="text-xs bg-[color:var(--sage-50)] p-2 rounded mt-1 overflow-auto">{JSON.stringify(val, null, 2)}</pre>;
                      else display = <span className="text-[color:var(--ink)]"> {String(val)}</span>;
                      return <div key={f.id}><span className="text-[color:var(--ink-soft)]">{f.label}:</span>{display}</div>;
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default AdminPage;
